import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Building2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import VendorCompliancePanel from '../components/vendors/VendorCompliancePanel';
import { apiService } from '../services/api';
import { Vendor, CreateVendorForm } from '../types';
import VendorForm from '../components/forms/VendorForm';
import { getComplianceStatusClasses } from '../utils/vendorCompliance';
import { useSubscription } from '../contexts/SubscriptionContext';

const VendorEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const vendorId = parseInt(id!);
  const initialTab = searchParams.get('tab') === 'compliance' ? 'compliance' : 'details';
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');
  const activeTab = hasVendorCompliance && initialTab === 'compliance' ? 'compliance' : 'details';

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => apiService.getVendor(vendorId),
    enabled: !!id
  });
  const vendor = data?.data as Vendor | undefined;

  // Fetch categories for the form
  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(1, 100),
  });
  const categories = categoriesResponse?.data?.data || [];

  const updateVendorMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateVendorForm }) => {
      console.log('VendorEditPage: updateVendorMutation.mutationFn called with:', { id, data });
      return apiService.updateVendor(id, data);
    },
    onSuccess: (response) => {
      console.log('VendorEditPage: updateVendorMutation.onSuccess called with:', response);
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      // Show success message
      toast.success(`Vendor "${response.data?.name || 'Unknown'}" updated successfully!`);
      const returnUrl = searchParams.get('returnUrl') || '/vendors';
      navigate(returnUrl);
    },
    onError: (error: any) => {
      console.error('VendorEditPage: updateVendorMutation.onError called with:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Unknown error';
      toast.error(`Error updating vendor: ${errorMessage}`);
    }
  });

  if (isLoading) {
    return <div className="p-6">Loading vendor...</div>;
  }
  if (error || !vendor) {
    return <div className="p-6">Vendor not found.</div>;
  }

  const handleSubmit = (data: CreateVendorForm) => {
    updateVendorMutation.mutate({ id: vendor.id, data });
  };

  const returnUrl = searchParams.get('returnUrl') || '/vendors';
  const backLabel = returnUrl.includes('/admin/vendor-compliance')
    ? 'Back to Vendor Compliance'
    : 'Back to Vendors';

  const handleCancel = () => {
    navigate(returnUrl);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{backLabel}</span>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Edit Vendor</h1>
              {hasVendorCompliance && vendor.complianceStatusLabel && (
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(vendor.complianceStatus, vendor.complianceStatusLabel)}`}>
                  {vendor.complianceStatusLabel}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{vendor.name}</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue={activeTab}>
        <TabsList>
          <TabsTrigger value="details">
            <Building2 className="h-4 w-4 mr-2 inline" />
            Details
          </TabsTrigger>
          {hasVendorCompliance && (
            <TabsTrigger value="compliance">
              <Shield className="h-4 w-4 mr-2 inline" />
              Compliance
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className="min-h-[28rem]">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Vendor Information</h2>
            </div>
            <div className="p-6">
              <VendorForm
                vendor={vendor}
                categories={categories}
                onSubmit={handleSubmit}
                isLoading={updateVendorMutation.isPending}
                error=""
              />
            </div>
          </div>
        </TabsContent>

        {hasVendorCompliance && (
        <TabsContent value="compliance" className="min-h-[28rem]">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Compliance</h2>
            </div>
            <div className="p-6">
              <VendorCompliancePanel vendorId={vendorId} />
            </div>
          </div>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default VendorEditPage;
